/**
 * BeezFy Premium FAQ Section JavaScript
 * Features: Lazy init, a11y, search, keyboard navigation
 * Performance: Zero deps, event delegation, IntersectionObserver
 */

class BeezFyFAQ {
  constructor(section) {
    this.section = section;
    this.list = section.querySelector('.bzf-faq__list');
    this.items = [...section.querySelectorAll('[data-faq-item]')];
    this.searchInput = section.querySelector('.bzf-faq__search');
    this.noResults = section.querySelector('.bzf-faq__no-results');

    this.settings = {
      openFirst: section.dataset.openFirst === 'true',
      allowMultiple: section.dataset.allowMultiple === 'true',
      showSearch: section.dataset.showSearch === 'true'
    };

    this.searchTimeout = null;
    this.init();
  }

  init() {
    this.bindEvents();
    if (this.settings.openFirst && this.items.length > 0) {
      this.openItem(this.items[0]);
    }
  }

  bindEvents() {
    // Event delegation for toggles
    this.section.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-faq-toggle]');
      if (toggle) {
        e.preventDefault();
        const item = toggle.closest('[data-faq-item]');
        this.toggleItem(item);
      }
    });

    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      const toggle = e.target.closest('[data-faq-toggle]');
      if (!toggle) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateItems(toggle, e.key === 'ArrowDown' ? 1 : -1);
      }
    });

    // Search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.filterItems(e.target.value);
        }, 200);
      });
    }

    // Theme Editor support
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => this.init());
      document.addEventListener('shopify:section:unload', () => this.destroy());
    }
  }

  toggleItem(item) {
    const isOpen = item.classList.contains('is-open');

    if (!this.settings.allowMultiple && !isOpen) {
      this.closeAllItems();
    }

    if (isOpen) {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  openItem(item) {
    const toggle = item.querySelector('[data-faq-toggle]');
    const wrapper = item.querySelector('.bzf-qa__content-wrapper');

    if (!toggle || !wrapper) return;

    toggle.setAttribute('aria-expanded', 'true');
    wrapper.removeAttribute('hidden');
    item.classList.add('is-open');

    // Smooth scroll into view on mobile
    if (window.innerWidth < 768) {
      setTimeout(() => {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }

  closeItem(item) {
    const toggle = item.querySelector('[data-faq-toggle]');
    const wrapper = item.querySelector('.bzf-qa__content-wrapper');

    if (!toggle || !wrapper) return;

    toggle.setAttribute('aria-expanded', 'false');
    wrapper.setAttribute('hidden', '');
    item.classList.remove('is-open');
  }

  closeAllItems() {
    this.items.forEach(item => this.closeItem(item));
  }

  navigateItems(currentToggle, direction) {
    const toggles = [...this.section.querySelectorAll('[data-faq-toggle]')];
    const currentIndex = toggles.indexOf(currentToggle);
    const nextIndex = currentIndex + direction;

    if (nextIndex >= 0 && nextIndex < toggles.length) {
      toggles[nextIndex].focus();
    }
  }

  filterItems(query) {
    const lowerQuery = query.toLowerCase().trim();
    let visibleCount = 0;

    this.items.forEach(item => {
      const toggle = item.querySelector('[data-faq-toggle]');
      const content = item.querySelector('.bzf-qa__answer');
      const tag = item.dataset.tag || '';

      const questionText = toggle ? toggle.textContent.toLowerCase() : '';
      const answerText = content ? content.textContent.toLowerCase() : '';
      const tagText = tag.toLowerCase();

      const matches = !lowerQuery ||
        questionText.includes(lowerQuery) ||
        answerText.includes(lowerQuery) ||
        tagText.includes(lowerQuery);

      if (matches) {
        item.classList.remove('is-hidden');
        visibleCount++;
      } else {
        item.classList.add('is-hidden');
        this.closeItem(item);
      }
    });

    if (this.noResults) {
      if (visibleCount === 0 && lowerQuery) {
        this.noResults.removeAttribute('hidden');
      } else {
        this.noResults.setAttribute('hidden', '');
      }
    }
  }

  destroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    // Remove event listeners if needed for cleanup
  }
}

// Lazy initialization with IntersectionObserver
const initFAQSections = () => {
  const sections = document.querySelectorAll('[data-section-type="bzf-faq"]');

  if (!sections.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !entry.target.dataset.faqInitialized) {
        new BeezFyFAQ(entry.target);
        entry.target.dataset.faqInitialized = 'true';
        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '100px'
  });

  sections.forEach(section => observer.observe(section));
};

// Auto-init on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFAQSections);
} else {
  initFAQSections();
}

// Theme Editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="bzf-faq"]');
    if (section && !section.dataset.faqInitialized) {
      new BeezFyFAQ(section);
      section.dataset.faqInitialized = 'true';
    }
  });
}
